import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { SHAPEFILE_FIELDS } from "constants/settings.constants";
import { mergeColumnIntoCategory } from "store/features";
import { parseShapefileProperty } from "utils/column/parseShapefileProperty";

import { GetShapefileFeatures } from "api/project";

import { IGroupByListColumn, isShapefileBoundaryProperty } from "models";
import { EntityKind } from "models/entityKind";
import { GeomT, ShapefileT } from "models/model";

import { useResolveShapefileColumn } from "./useResolveShapefileColumn";

const getDataType = (attribute: unknown): string => {
  if (typeof attribute === "number") return "Number";
  if (typeof attribute === "boolean") return "Text";
  if (typeof attribute === "string") {
    return isNaN(Date.parse(attribute)) ? "Text" : "Date";
  }
  return "Unknown";
};

const findFirstNonNullValue = (geoms: GeomT[], column: string): unknown => {
  for (const geom of geoms ?? []) {
    const value = geom?.Properties?.[column];
    if (value !== null && value !== undefined) return value;
  }
  return null;
};

const fetchShapefile = (shapefileId: string): Promise<ShapefileT> =>
  new Promise((resolve, reject) => {
    GetShapefileFeatures(shapefileId, resolve, reject);
  });

/**
 * Loads a single shapefile field's column on demand (matching the column
 * ShapefileCategory builds when a shapefile is picked in the group-by tree) and
 * merges it into the group-by category list, so a shapefile field persisted as
 * a default can be applied without the user first opening it in group-by.
 * Returns the built column (also for immediate use before the store re-renders),
 * or null if the key isn't a shapefile field or the fetch fails.
 */
export const useLoadShapefileColumn = (
  entityKind: EntityKind
): ((property: string) => Promise<Nullable<IGroupByListColumn>>) => {
  const dispatch = useDispatch();
  const resolveShapefileColumn = useResolveShapefileColumn();

  return useCallback(
    async (property: string): Promise<Nullable<IGroupByListColumn>> => {
      const parsed = parseShapefileProperty(property);
      if (!parsed) return null;

      // Rebased on the list in the store at dispatch time: two columns
      // resolving concurrently would otherwise each splice their own stale
      // copy of the whole list and drop the other's.
      const mergeIntoCategoryList = (column: IGroupByListColumn) => {
        dispatch(mergeColumnIntoCategory(entityKind, SHAPEFILE_FIELDS, column));
      };

      // The boundary key names the shapefile itself, which nothing focuses on
      // any more — it belongs to the shapefile filtering toggle, which builds
      // its predicate without a column. A key left over in a saved focus
      // resolves to nothing rather than to a column named after the reserved
      // sentinel.
      if (isShapefileBoundaryProperty(property)) {
        return null;
      }

      let shapefile: ShapefileT;
      try {
        shapefile = await fetchShapefile(parsed.shapefileId);
      } catch {
        return null;
      }

      const firstValue = findFirstNonNullValue(shapefile.geoms, parsed.columnName);
      const dataType = getDataType(firstValue);
      // Prefer the shapefile name from the project tree (what group-by shows and
      // stores in the title); fall back to the features-endpoint fields.
      const subgroup =
        resolveShapefileColumn(property)?.subgroup || shapefile.title || shapefile.name;
      const column = {
        title: parsed.columnName,
        id: parsed.columnName,
        canBin: dataType === "Number",
        dataType,
        canNormalize: false,
        group: SHAPEFILE_FIELDS,
        defaultUnit: "",
        normalizeByDefault: 1,
        subgroup,
        property
      } as IGroupByListColumn;

      mergeIntoCategoryList(column);

      return column;
    },
    [dispatch, entityKind, resolveShapefileColumn]
  );
};
